import { useState } from "react";
import { useNotification } from "../../../context/notification-provider";
import {
  testValueExpression,
  saveValueExpression,
  updateValueExpression,
  deleteValueExpression,
} from "../services/template-service";
import type { ValueExpressionItem } from "../types/api-types";
import type { TestResult } from "./types";

interface UseRegexManagementProps {
  templateId: number;
  validRegexList: ValueExpressionItem[];
  mutate: () => void;
}

const useRegexManagement = ({
  templateId,
  validRegexList,
  mutate,
}: UseRegexManagementProps) => {
  const { showNotification } = useNotification();
  const [openDialog, setOpenDialog] = useState(false);
  const [newRegex, setNewRegex] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editId, setEditId] = useState<number>(0);
  const [testValue, setTestValue] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isTestSuccessful, setIsTestSuccessful] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewRegex("");
    setEditIndex(null);
    setEditId(0);
    setTestValue("");
    setTestResult(null);
    setIsTestSuccessful(false);
    setSaveError(null);
  };

  const handleOpenEditDialog = (index: number, item: ValueExpressionItem) => {
    setOpenDialog(true);
    setNewRegex(item.expression);
    setEditIndex(index);
    setEditId(item.id);
    setTestValue("");
    setTestResult(null);
    setIsTestSuccessful(false);
    setSaveError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewRegex("");
    setEditIndex(null);
    setEditId(0);
    setTestValue("");
    setTestResult(null);
    setIsTestSuccessful(false);
    setSaveError(null);
  };

  const handleTestExpression = async () => {
    if (!newRegex.trim() || !testValue.trim()) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await testValueExpression({
        testValue: testValue.trim(),
        id: 0,
        templateId,
        expression: newRegex.trim(),
      });

      setTestResult(result);
      setIsTestSuccessful(result.isValid && result.matches);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to test expression. Please try again.";
      setTestResult({
        isValid: false,
        matches: false,
        message: errorMessage,
      });
      setIsTestSuccessful(false);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveRegex = async () => {
    if (!newRegex.trim() || !isTestSuccessful) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (editIndex === null) {
        await saveValueExpression({
          id: 0,
          templateId,
          expression: newRegex.trim(),
        });
      } else {
        await updateValueExpression({
          id: editId,
          templateId,
          expression: newRegex.trim(),
        });
      }

      // Refresh the data from the API
      mutate();

      showNotification({
        message:
          editIndex === null
            ? "Expression added successfully"
            : "Expression updated successfully",
        severity: "success",
      });

      handleCloseDialog();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to save expression. Please try again.";
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveRegex = (index: number) => {
    setIndexToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (indexToDelete === null) return;

    const item = validRegexList[indexToDelete];
    setIsDeleting(true);

    try {
      await deleteValueExpression({
        id: item.id,
        templateId,
        expression: item.expression,
      });

      // Refresh the data from the API
      mutate();

      showNotification({
        message: "Expression deleted successfully",
        severity: "success",
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete expression. Please try again.";

      showNotification({
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setIndexToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setIndexToDelete(null);
  };

  return {
    // Dialog state
    openDialog,
    newRegex,
    setNewRegex,
    editIndex,
    testValue,
    setTestValue,
    testResult,
    setTestResult,
    isTestSuccessful,
    setIsTestSuccessful,
    isTesting,
    isSaving,
    saveError,

    // Delete state
    deleteConfirmOpen,
    indexToDelete,
    isDeleting,

    // Handler functions
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleTestExpression,
    handleSaveRegex,
    handleRemoveRegex,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useRegexManagement;
